import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, Pencil, Trash2, FolderOpen, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Projects() {
  const [projects, setProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    client_name: '',
    client_project_number: '',
    westaw_project_number: ''
  });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to fetch projects');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (editingId) {
        await axios.put(`${BACKEND_URL}/api/projects/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Project updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/projects`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Project created successfully');
      }
      
      setFormData({
        client_name: '',
        client_project_number: '',
        westaw_project_number: ''
      });
      setEditingId(null);
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save project');
    }
  };

  const handleEdit = (project) => {
    setFormData({
      client_name: project.client_name,
      client_project_number: project.client_project_number,
      westaw_project_number: project.westaw_project_number
    });
    setEditingId(project.id);
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project deleted successfully');
      fetchProjects();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const handleCancel = () => {
    setFormData({
      client_name: '',
      client_project_number: '',
      westaw_project_number: ''
    });
    setEditingId(null);
  };

  const filteredProjects = projects.filter(project => 
    project.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.client_project_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.westaw_project_number.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight font-heading" data-testid="projects-title">
          Projects
        </h1>
        <p className="text-slate-600 mt-2">Manage client projects and WESTAW project numbers</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-xl font-semibold">
              {editingId ? 'Edit Project' : 'Add New Project'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="project-form">
              <div className="space-y-2">
                <Label htmlFor="client_name">Client Name</Label>
                <Input
                  id="client_name"
                  data-testid="project-client-name-input"
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  placeholder="Enter client name"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="client_project_number">Client Project Number</Label>
                <Input
                  id="client_project_number"
                  data-testid="project-client-number-input"
                  value={formData.client_project_number}
                  onChange={(e) => setFormData({ ...formData, client_project_number: e.target.value })}
                  placeholder="Enter client project number"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="westaw_project_number">WESTAW Project Number</Label>
                <Input
                  id="westaw_project_number"
                  data-testid="project-westaw-number-input"
                  value={formData.westaw_project_number}
                  onChange={(e) => setFormData({ ...formData, westaw_project_number: e.target.value })}
                  placeholder="Enter WESTAW project number"
                  required
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" className="flex-1 bg-slate-900 hover:bg-slate-800" data-testid="submit-project-button">
                  <Plus className="w-4 h-4 mr-2" />
                  {editingId ? 'Update Project' : 'Add Project'}
                </Button>
                {editingId && (
                  <Button type="button" variant="outline" onClick={handleCancel}>
                    Cancel
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Projects List */}
        <Card className="lg:col-span-2 bg-white border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-semibold">All Projects</CardTitle>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search projects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="project-search-input"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="rounded-xl overflow-hidden">
              <table className="w-full" data-testid="projects-table">
                <thead className="bg-slate-50">
                  <tr className="border-b border-slate-200">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Client Name</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Client Project #</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">WESTAW Project #</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjects.map((project) => (
                    <tr key={project.id} className="hover:bg-slate-50/50 border-b border-slate-100 last:border-0" data-testid={`project-row-${project.id}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <FolderOpen className="w-4 h-4 text-amber-500" />
                          <span className="font-medium text-slate-900">{project.client_name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">{project.client_project_number}</td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 bg-sky-100 text-sky-700 rounded text-sm font-medium">
                          {project.westaw_project_number}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(project)}
                            className="text-slate-600 hover:text-slate-900"
                            data-testid={`edit-project-${project.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(project.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-project-${project.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredProjects.length === 0 && (
                <div className="p-8 text-center text-slate-600">
                  {searchTerm ? 'No projects found matching your search' : 'No projects added yet'}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default Projects;
